<template>
  <dashboard-layout title="Profile" :kicker="kickerLabel" :items="navItems">
    <partner-pin-location-modal
      ref="profilePinModal"
      modal-id="ProfilePartnerPinModal"
      map-element-id="profile-partner-pin-map"
      @confirmed="onProfilePinConfirmed"
    />

    <div class="pp-card bg-white rounded-3 border shadow-sm p-3 p-md-4">
      <p class="inter-regular-family text-muted text-full-11 mb-4">
        Details are loaded from your approved application. Your email is tied to your account and cannot be changed here.
        New photo or CV uploads stay as originally submitted unless we add uploads later.
      </p>

      <div v-if="loadError" class="alert alert-warning py-2 px-3 small mb-3" role="alert">
        {{ loadError }}
      </div>

      <div v-if="loading" class="text-center py-5 inter-regular-family text-muted">
        Loading profile…
      </div>

      <div v-if="!loading && isSalon && salonPhotoUrls.length" class="mb-4">
        <h4 class="pp-sec-title filson-black-family">Salon photos on file</h4>
        <div class="d-flex flex-wrap gap-2">
          <a
            v-for="(u, i) in salonPhotoUrls"
            :key="'sp-' + i"
            :href="u"
            target="_blank"
            rel="noopener noreferrer"
            class="small text-decoration-none"
          >Open photo {{ i + 1 }}</a>
        </div>
      </div>

      <div v-if="!loading && isGroomer && groomerWorkPhotoUrls.length" class="mb-4">
        <h4 class="pp-sec-title filson-black-family">Work photos on file</h4>
        <div class="d-flex flex-wrap gap-2">
          <a
            v-for="(u, i) in groomerWorkPhotoUrls"
            :key="'gw-' + i"
            :href="u"
            target="_blank"
            rel="noopener noreferrer"
            class="small text-decoration-none"
          >Open photo {{ i + 1 }}</a>
        </div>
      </div>

      <div v-if="!loading && isGroomer && groomerCvUrl" class="mb-4">
        <h4 class="pp-sec-title filson-black-family">CV on file</h4>
        <a :href="groomerCvUrl" target="_blank" rel="noopener noreferrer" class="small text-decoration-none">Download CV</a>
      </div>

      <form v-if="!loading" @submit.prevent="saveProfile">
        <!-- Step 1 (shared) -->
        <h3 class="pp-sec-title filson-black-family">Contact</h3>
        <div class="row g-2 mb-3">
          <div class="col-md-6">
            <label class="pp-label">Country</label>
            <select v-model="form.countrySlug" class="pp-input pp-select" :disabled="isCountryLocked" required>
              <option value="" disabled>Select</option>
              <option v-for="c in activeCountries" :key="c.slug" :value="c.slug">{{ c.name }}</option>
            </select>
            <div v-if="isCountryLocked" class="text-muted small mt-1">
              Country is locked for this account.
            </div>
          </div>
          <div class="col-md-6">
            <label class="pp-label">First name</label>
            <input v-model.trim="form.firstName" type="text" class="pp-input" required>
          </div>
          <div class="col-md-6">
            <label class="pp-label">Last name</label>
            <input v-model.trim="form.lastName" type="text" class="pp-input" required>
          </div>
          <div class="col-md-6">
            <label class="pp-label">Email</label>
            <input :value="form.email" type="email" class="pp-input pp-input--readonly" readonly tabindex="-1">
          </div>
          <div class="col-md-6">
            <label class="pp-label">{{ isGroomer ? 'Mobile phone number' : 'Business phone number' }}</label>
            <input v-model.trim="form.phone" type="tel" class="pp-input" required>
          </div>

          <div class="col-12">
            <h3 class="pp-sec-title filson-black-family mb-2">Pin location</h3>
            <p class="text-muted text-full-11 inter-regular-family mb-2">
              Same as the Location page: drop a map pin for your country. Saved when you click “Save profile”.
            </p>
            <div v-if="servicePinLoadError" class="alert alert-warning py-2 px-3 small mb-2" role="alert">
              {{ servicePinLoadError }}
            </div>
            <label class="pp-label">Pinned address</label>
            <input
              :value="servicePinSummaryDisplay"
              type="text"
              class="pp-input mb-2"
              placeholder="No pin set yet — open the map"
              readonly
              @click="openProfilePinModal"
            >
            <div class="row g-2 mb-1">
              <div class="col-md-6">
                <label class="pp-label">Latitude</label>
                <input :value="servicePin.lat != null ? servicePin.lat : ''" type="text" class="pp-input pp-input--readonly" readonly tabindex="-1">
              </div>
              <div class="col-md-6">
                <label class="pp-label">Longitude</label>
                <input :value="servicePin.lng != null ? servicePin.lng : ''" type="text" class="pp-input pp-input--readonly" readonly tabindex="-1">
              </div>
            </div>
            <div class="d-flex flex-wrap gap-2 mt-2">
              <button type="button" class="pp-secondary-btn inter-bold-family text-uppercase text-full-11" @click="openProfilePinModal">
                Open map
              </button>
              <button type="button" class="pp-secondary-btn inter-bold-family text-uppercase text-full-11" @click="useBrowserPinLocation">
                Use my location
              </button>
            </div>
          </div>

          <template v-if="isGroomer">
            <div class="col-12">
              <label class="pp-label">Mailing address</label>
              <input v-model.trim="form.mailingAddress" type="text" class="pp-input" required>
            </div>
            <div class="col-md-6">
              <label class="pp-label">Years of grooming experience</label>
              <select v-model="form.yearsExperience" class="pp-input pp-select" required>
                <option value="" disabled>Select</option>
                <option v-for="o in yearsExperienceOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </div>
          </template>
        </div>

        <!-- Salon step 2 -->
        <template v-if="isSalon">
          <h3 class="pp-sec-title filson-black-family">Salon specifications</h3>
          <div class="row g-2 mb-3">
            <div class="col-12">
              <label class="pp-label">Legal salon name</label>
              <input v-model.trim="form.legalSalonName" type="text" class="pp-input" required>
            </div>
            <div class="col-12">
              <label class="pp-label">Physical salon address</label>
              <input v-model.trim="form.physicalAddress" type="text" class="pp-input" required>
            </div>
            <div class="col-md-6">
              <label class="pp-label">EIN / Tax ID number</label>
              <input v-model.trim="form.taxId" type="text" class="pp-input">
            </div>
            <div class="col-md-6">
              <label class="pp-label">Number of grooming stations</label>
              <select v-model="form.groomingStations" class="pp-input pp-select" required>
                <option value="" disabled>Select</option>
                <option v-for="o in groomingStationsOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="pp-label">Number of bathing tubs</label>
              <select v-model="form.bathingTubs" class="pp-input pp-select" required>
                <option value="" disabled>Select</option>
                <option v-for="o in bathingTubsOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="pp-label">Groomers at location</label>
              <select v-model="form.groomersCount" class="pp-input pp-select" required>
                <option value="" disabled>Select</option>
                <option v-for="o in groomersCountOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </div>
            <div class="col-12">
              <span class="pp-label d-block mb-2">Business liability insurance</span>
              <label class="me-4"><input v-model="form.hasInsurance" type="radio" value="yes" required> Yes</label>
              <label><input v-model="form.hasInsurance" type="radio" value="no"> No</label>
            </div>
          </div>

          <h3 class="pp-sec-title filson-black-family">Hours &amp; services</h3>
          <p class="text-muted text-full-11 inter-regular-family">At least one day must be open.</p>
          <div class="pp-hours mb-3">
            <div
              v-for="d in weekDays"
              :key="d.key"
              class="pp-hours-row"
              :class="{ 'pp-hours-row--off': !d.model.open }"
            >
              <label class="pp-hours-day">
                <input v-model="d.model.open" type="checkbox">
                <span>{{ d.label }}</span>
              </label>
              <div class="pp-hours-times">
                <input v-model="d.model.start" type="time" class="pp-input pp-time" :disabled="!d.model.open || d.model.fullDay">
                <input v-model="d.model.end" type="time" class="pp-input pp-time" :disabled="!d.model.open || d.model.fullDay">
              </div>
              <label class="pp-fullday">
                <input v-model="d.model.fullDay" type="checkbox" :disabled="!d.model.open">
                Full day
              </label>
            </div>
          </div>
          <div class="mb-2 pp-label">Services offered</div>
          <div class="pp-checks mb-2">
            <label v-for="s in serviceOfferedDefs" :key="s.id" class="pp-check">
              <input v-model="form.serviceOffered" type="checkbox" :value="s.id">
              {{ s.label }}
            </label>
          </div>
          <div class="mb-3">
            <label class="pp-label">Other services (if applicable)</label>
            <textarea v-model.trim="form.serviceOtherText" class="pp-input pp-textarea" rows="3"></textarea>
          </div>

          <h3 class="pp-sec-title filson-black-family">Web &amp; referral</h3>
          <div class="row g-2 mb-3">
            <div class="col-12">
              <label class="pp-label">Website URL</label>
              <input v-model.trim="form.websiteUrl" type="text" class="pp-input">
            </div>
            <div class="col-md-6">
              <label class="pp-label">Facebook</label>
              <input v-model.trim="form.facebook" type="text" class="pp-input">
            </div>
            <div class="col-md-6">
              <label class="pp-label">TikTok</label>
              <input v-model.trim="form.tiktok" type="text" class="pp-input">
            </div>
            <div class="col-md-6">
              <label class="pp-label">Referral</label>
              <select v-model="form.referral" class="pp-input pp-select" required>
                <option value="" disabled>Select</option>
                <option v-for="o in referralOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </div>
          </div>
        </template>

        <!-- Groomer step 2 -->
        <template v-if="isGroomer">
          <h3 class="pp-sec-title filson-black-family">Professional profile</h3>
          <div class="mb-3">
            <div class="pp-label mb-2">Primary service type</div>
            <div class="d-flex flex-wrap gap-2">
              <button
                type="button"
                class="pp-pill"
                :class="{ 'pp-pill--on': form.primaryServiceType === 'pet_groomer' }"
                @click="form.primaryServiceType = 'pet_groomer'"
              >
                Pet groomer
              </button>
              <button
                type="button"
                class="pp-pill"
                :class="{ 'pp-pill--on': form.primaryServiceType === 'pet_bather' }"
                @click="form.primaryServiceType = 'pet_bather'"
              >
                Pet bather
              </button>
            </div>
          </div>
          <div class="row g-2 mb-3">
            <div class="col-md-6">
              <label class="pp-label">Pets groomed (last 2 years)</label>
              <select v-model="form.petsGroomed" class="pp-input pp-select" required>
                <option value="" disabled>Select</option>
                <option v-for="o in petsGroomedOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
              </select>
            </div>
          </div>
          <div class="mb-3">
            <div class="pp-label mb-2">Pets you service</div>
            <div class="pp-checks">
              <label v-for="p in petsServicedOptions" :key="p.value" class="pp-check">
                <input v-model="form.petsServiced" type="checkbox" :value="p.value">
                {{ p.label }}
              </label>
            </div>
          </div>
          <div class="mb-3">
            <span class="pp-label d-block mb-2">Grooming certifications</span>
            <label class="me-4"><input v-model="form.hasCertifications" type="radio" value="yes"> Yes</label>
            <label><input v-model="form.hasCertifications" type="radio" value="no"> No</label>
          </div>
          <div v-if="form.hasCertifications === 'yes'" class="mb-3">
            <label class="pp-label">Certification details</label>
            <input v-model.trim="form.certifications" type="text" class="pp-input">
          </div>
          <div class="mb-3">
            <span class="pp-label d-block mb-2">Valid driver&apos;s license</span>
            <label class="me-4"><input v-model="form.hasDriversLicense" type="radio" value="yes"> Yes</label>
            <label><input v-model="form.hasDriversLicense" type="radio" value="no"> No</label>
          </div>
          <div class="mb-3">
            <div class="pp-label mb-2">Preferred service types</div>
            <div class="pp-checks">
              <label v-for="p in preferredServiceOptions" :key="p.value" class="pp-check">
                <input v-model="form.preferredServiceType" type="checkbox" :value="p.value">
                {{ p.label }}
              </label>
            </div>
          </div>
          <div class="mb-3">
            <span class="pp-label d-block mb-2">Grooming tools</span>
            <label class="me-4"><input v-model="form.hasTools" type="radio" value="yes"> Yes</label>
            <label><input v-model="form.hasTools" type="radio" value="no"> No</label>
          </div>
          <div class="mb-3">
            <span class="pp-label d-block mb-2">SSN or Tax ID</span>
            <label class="me-4"><input v-model="form.hasTaxId" type="radio" value="yes"> Yes</label>
            <label><input v-model="form.hasTaxId" type="radio" value="no"> No</label>
          </div>

          <h3 class="pp-sec-title filson-black-family">Experience &amp; social</h3>
          <div class="mb-3">
            <span class="pp-label d-block mb-2">Currently working (salon / mobile / in-home)</span>
            <label class="me-4"><input v-model="form.workCurrently" type="radio" value="yes"> Yes</label>
            <label><input v-model="form.workCurrently" type="radio" value="no"> No</label>
          </div>
          <div class="mb-3">
            <span class="pp-label d-block mb-2">References available</span>
            <label class="me-4"><input v-model="form.hasReferences" type="radio" value="yes"> Yes</label>
            <label><input v-model="form.hasReferences" type="radio" value="no"> No</label>
          </div>
          <div class="row g-2 mb-3">
            <div class="col-md-6">
              <label class="pp-label">Instagram</label>
              <input v-model.trim="form.groomerInstagram" type="text" class="pp-input">
            </div>
            <div class="col-md-6">
              <label class="pp-label">Facebook</label>
              <input v-model.trim="form.groomerFacebook" type="text" class="pp-input">
            </div>
            <div class="col-md-6">
              <label class="pp-label">TikTok</label>
              <input v-model.trim="form.groomerTiktok1" type="text" class="pp-input">
            </div>
            <div class="col-md-6">
              <label class="pp-label">TikTok (alternate)</label>
              <input v-model.trim="form.groomerTiktok2" type="text" class="pp-input">
            </div>
            <div class="col-12">
              <label class="pp-label">Where did you learn grooming?</label>
              <textarea v-model.trim="form.groomerLearnGrooming" class="pp-input pp-textarea" rows="4"></textarea>
            </div>
          </div>
        </template>

        <button type="submit" class="pp-save inter-bold-family text-uppercase text-full-11" :disabled="saving">
          {{ saving ? 'Saving…' : 'Save profile' }}
        </button>
      </form>
    </div>
  </dashboard-layout>
</template>

<script>
/* global google */
import DashboardLayout from './DashboardLayout.vue';
import PartnerPinLocationModal from './PartnerPinLocationModal.vue';
import { SALON_DASH_NAV, GROOMER_DASH_NAV } from '@/utils/dashboardNav';
import {
  readPartnerAuth,
  readPartnerApplicationSnapshot,
  writePartnerApplicationSnapshot,
  mergePartnerApplicationSnapshot,
  createEmptyPartnerApplicationForm,
  clearPartnerSession,
} from '@/utils/partnerProfileStorage';

export default {
  name: 'PartnerDashboardProfile',
  components: { DashboardLayout, PartnerPinLocationModal },
  props: {
    partnerRole: {
      type: String,
      required: true,
      validator: (v) => v === 'salon' || v === 'groomer',
    },
  },
  data() {
    return {
      salonPage: null,
      activeCountries: [],
      countriesLoading: false,
      initialCountrySlug: '',
      saving: false,
      loading: true,
      loadError: '',
      form: createEmptyPartnerApplicationForm(this.partnerRole),
      servicePin: {
        address: '',
        lat: null,
        lng: null,
      },
      servicePinLoadError: '',
    };
  },
  computed: {
    servicePinSummaryDisplay() {
      const lat = Number(this.servicePin.lat);
      const lng = Number(this.servicePin.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
      const addr = String(this.servicePin.address || '').trim();
      return addr ? `${addr} (${lat}, ${lng})` : `${lat}, ${lng}`;
    },
    servicePinComplete() {
      const lat = Number(this.servicePin.lat);
      const lng = Number(this.servicePin.lng);
      return Number.isFinite(lat) && Number.isFinite(lng) && String(this.servicePin.address || '').trim().length > 0;
    },
    salonPhotoUrls() {
      const u = this.form.salon_photo_urls;
      return Array.isArray(u) ? u.filter(Boolean) : [];
    },
    groomerWorkPhotoUrls() {
      const u = this.form.groomer_work_photo_urls;
      return Array.isArray(u) ? u.filter(Boolean) : [];
    },
    groomerCvUrl() {
      const u = this.form.groomer_cv_url;
      return typeof u === 'string' && u.trim() ? u.trim() : '';
    },
    kickerLabel() {
      return this.isSalon ? 'Salon dashboard' : 'Groomer dashboard';
    },
    navItems() {
      return this.isSalon ? SALON_DASH_NAV : GROOMER_DASH_NAV;
    },
    isSalon() {
      return this.partnerRole === 'salon';
    },
    isGroomer() {
      return this.partnerRole === 'groomer';
    },
    isCountryLocked() {
      const init = String(this.initialCountrySlug || '').toLowerCase().trim();
      const cur = String(this.form.countrySlug || '').toLowerCase().trim();
      return Boolean(init && cur && init === cur);
    },
    weekDays() {
      const defs = [
        { key: 'monday', label: 'Monday' },
        { key: 'tuesday', label: 'Tuesday' },
        { key: 'wednesday', label: 'Wednesday' },
        { key: 'thursday', label: 'Thursday' },
        { key: 'friday', label: 'Friday' },
        { key: 'saturday', label: 'Saturday' },
        { key: 'sunday', label: 'Sunday' },
      ];
      return defs.map((d) => ({ ...d, model: this.form.hours?.[d.key] }));
    },
    serviceOfferedDefs() {
      return [
        { id: 'full_groom', label: 'Full groom' },
        { id: 'bath_brush', label: 'Bath & brush' },
        { id: 'nail_trim', label: 'Nail trim only' },
        { id: 'others', label: 'Others' },
      ];
    },
    yearsExperienceOptions() {
      const api = this.salonPage?.join_step1_groomer_experience_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({
          value: o?.value != null ? String(o.value) : String(idx + 1),
          label: o?.label != null ? String(o.label) : '',
        })).filter((o) => o.label.trim())
        : [];
      const fallback = [
        { value: '0-1', label: '0–1 years' },
        { value: '2-4', label: '2–4 years' },
        { value: '5-7', label: '5–7 years' },
        { value: '8-10', label: '8–10 years' },
        { value: '10+', label: '10+ years' },
      ];
      return normalized.length ? normalized : fallback;
    },
    groomingStationsOptions() {
      const api = this.salonPage?.join_step2_grooming_station_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = Array.from({ length: 10 }).map((_, i) => ({ value: String(i + 1), label: String(i + 1) }));
      return normalized.length ? normalized : fallback;
    },
    bathingTubsOptions() {
      const api = this.salonPage?.join_step2_bathing_tub_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = Array.from({ length: 10 }).map((_, i) => ({ value: String(i + 1), label: String(i + 1) }));
      return normalized.length ? normalized : fallback;
    },
    groomersCountOptions() {
      const api = this.salonPage?.join_step2_groomers_count_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = Array.from({ length: 21 }).map((_, i) => ({ value: String(i), label: i === 20 ? '20+' : String(i) }));
      return normalized.length ? normalized : fallback;
    },
    referralOptions() {
      const api = this.salonPage?.join_step3_referral_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = [
        { value: 'google', label: 'Google' },
        { value: 'facebook', label: 'Facebook' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'friend', label: 'Friend / referral' },
        { value: 'other', label: 'Other' },
      ];
      return normalized.length ? normalized : fallback;
    },
    petsGroomedOptions() {
      const api = this.salonPage?.join_groomer_step2_pets_groomed_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = [
        { value: '0-25', label: '0–25' },
        { value: '26-50', label: '26–50' },
        { value: '51-100', label: '51–100' },
        { value: '101-200', label: '101–200' },
        { value: '200+', label: '200+' },
      ];
      return normalized.length ? normalized : fallback;
    },
    petsServicedOptions() {
      const api = this.salonPage?.join_groomer_step2_pets_serviced_options;
      const normalized = Array.isArray(api)
        ? api.map((o) => ({ value: String(o?.value ?? ''), label: String(o?.label ?? '') })).filter((o) => o.value && o.label.trim())
        : [];
      const fallback = [
        { value: 'dogs', label: 'Dogs' },
        { value: 'cats', label: 'Cats' },
        { value: 'rabbits', label: 'Rabbits' },
        { value: 'parrots', label: 'Parrots' },
      ];
      return normalized.length ? normalized : fallback;
    },
    preferredServiceOptions() {
      const api = this.salonPage?.join_groomer_step2_preferred_service_options;
      const normalized = Array.isArray(api)
        ? api.map((o) => ({ value: String(o?.value ?? ''), label: String(o?.label ?? '') })).filter((o) => o.value && o.label.trim())
        : [];
      const fallback = [
        { value: 'mobile', label: 'Mobile*' },
        { value: 'in_home', label: 'In-home' },
      ];
      return normalized.length ? normalized : fallback;
    },
  },
  async mounted() {
    const auth = readPartnerAuth();
    if (!auth?.joinType || !auth.email || !auth.token) {
      this.$router.replace({ path: '/login' });
      return;
    }
    const jt = String(auth.joinType).toLowerCase();
    if (jt !== this.partnerRole) {
      this.$router.replace({ path: `/dashboard/${jt}` });
      return;
    }
    await Promise.all([
      this.fetchSalonJoinConfig(),
      this.fetchActiveCountries(),
      this.loadProfileFromApi(auth),
    ]);
    await this.loadServicePinFromApi();
  },
  methods: {
    async fetchActiveCountries() {
      if (this.countriesLoading) return;
      this.countriesLoading = true;
      try {
        const res = await this.$axios.get('/get-countries');
        const list = Array.isArray(res?.data?.countries) ? res.data.countries : [];
        this.activeCountries = list
          .map((c) => ({
            slug: String(c?.slug || '').toLowerCase().trim(),
            name: String(c?.name || '').trim(),
          }))
          .filter((c) => c.slug && c.name);
      } catch {
        this.activeCountries = [];
      } finally {
        this.countriesLoading = false;
      }
    },
    stripProfilePayloadForSave(form) {
      const raw = JSON.parse(JSON.stringify(form));
      delete raw.salon_photo_urls;
      delete raw.groomer_work_photo_urls;
      delete raw.groomer_cv_url;
      // Pin is persisted via PATCH /partner/location only — avoid overwriting server coords with stale snapshot.
      delete raw.locations;
      delete raw.pin_lat;
      delete raw.pin_lng;
      delete raw.pin_address;
      return raw;
    },
    hydrateFormLocal(auth) {
      const snap = readPartnerApplicationSnapshot();
      const merged = mergePartnerApplicationSnapshot(snap || {}, this.partnerRole);
      merged.email = String(auth.email || '');
      this.form = merged;
    },
    async loadProfileFromApi(auth) {
      this.loading = true;
      this.loadError = '';
      try {
        const res = await this.$axios.get('/partner/profile');
        if (res?.data?.success && res.data.profile) {
          const merged = mergePartnerApplicationSnapshot(res.data.profile, this.partnerRole);
          merged.email = String(res.data.profile.email || auth.email || '');
          this.initialCountrySlug = String(merged.countrySlug || '').toLowerCase().trim();
          this.form = merged;
          writePartnerApplicationSnapshot(merged);
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          clearPartnerSession();
          this.$router.replace({ path: '/login' });
          return;
        }
        this.loadError = e?.response?.data?.message || 'Could not load profile from server.';
        this.hydrateFormLocal(auth);
      } finally {
        if (!this.initialCountrySlug) {
          this.initialCountrySlug = String(this.form.countrySlug || '').toLowerCase().trim();
        }
        this.loading = false;
      }
    },
    async loadServicePinFromApi() {
      this.servicePinLoadError = '';
      try {
        const res = await this.$axios.get('/partner/location');
        if (res?.data?.success && res.data.location) {
          const loc = res.data.location || {};
          this.servicePin = {
            address: String(loc.address || ''),
            lat: loc.lat != null ? Number(loc.lat) : null,
            lng: loc.lng != null ? Number(loc.lng) : null,
          };
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          clearPartnerSession();
          this.$router.replace({ path: '/login' });
          return;
        }
        this.servicePinLoadError = e?.response?.data?.message || 'Could not load pin location.';
      }
    },
    openProfilePinModal() {
      this.$refs.profilePinModal?.open({
        lat: this.servicePin.lat,
        lng: this.servicePin.lng,
        address: this.servicePin.address,
      });
    },
    onProfilePinConfirmed({ address, lat, lng }) {
      this.servicePin.address = address;
      this.servicePin.lat = lat;
      this.servicePin.lng = lng;
      this.$toast?.success('Pin updated — remember to save profile.');
    },
    useBrowserPinLocation() {
      if (!navigator.geolocation) {
        this.$toast?.error('Geolocation is not supported in this browser.');
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos?.coords?.latitude;
          const lng = pos?.coords?.longitude;
          if (lat == null || lng == null) return;
          this.servicePin.lat = Number(lat);
          this.servicePin.lng = Number(lng);
          this.fillServicePinAddressFromCoords(this.servicePin.lat, this.servicePin.lng);
        },
        () => {
          this.$toast?.error('Could not read your current location.');
        },
        { enableHighAccuracy: true, timeout: 12000 }
      );
    },
    fillServicePinAddressFromCoords(lat, lng) {
      if (!window.google?.maps?.Geocoder) {
        this.$toast?.error('Maps not loaded yet — use Open map instead.');
        return;
      }
      let countryCode = '';
      try {
        const stored = JSON.parse(localStorage.getItem('selected_country') || 'null');
        countryCode = (stored?.code || '').toString().trim();
      } catch (_) {
        countryCode = '';
      }
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        {
          location: { lat: Number(lat), lng: Number(lng) },
          ...(countryCode ? { region: countryCode } : {}),
        },
        (results, status) => {
          if (status === 'OK' && results && results[0]) {
            this.servicePin.address = results[0].formatted_address || '';
            this.$toast?.success('Coordinates set — remember to save profile.');
          }
        }
      );
    },
    async fetchSalonJoinConfig() {
      try {
        const res = await this.$axios.post('/get-salon-page', {
          website_name: this.activeWebsiteName,
        });
        this.salonPage = res?.data?.salon_page ?? null;
      } catch {
        // fallbacks in computed options
      }
    },
    async saveProfile() {
      if (this.isSalon) {
        if (!this.validateSalonHours()) {
          this.$toast?.error('Please set valid hours for at least one open day.');
          return;
        }
        if (!this.validateSalonExtras()) return;
      } else if (!this.validateGroomerProfile()) {
        return;
      }
      this.saving = true;
      try {
        const profile = this.stripProfilePayloadForSave({
          ...this.form,
          joinType: this.partnerRole,
        });
        const res = await this.$axios.patch('/partner/profile', { profile });
        if (res?.data?.success && res.data.profile) {
          const merged = mergePartnerApplicationSnapshot(res.data.profile, this.partnerRole);
          this.form = merged;
          writePartnerApplicationSnapshot(merged);
        }

        if (this.servicePinComplete) {
          try {
            const locRes = await this.$axios.patch('/partner/location', {
              location: {
                address: String(this.servicePin.address || '').trim(),
                lat: Number(this.servicePin.lat),
                lng: Number(this.servicePin.lng),
              },
            });
            if (locRes?.data?.success) {
              await this.loadServicePinFromApi();
            }
          } catch (le) {
            const status = le?.response?.status;
            if (status === 401) {
              clearPartnerSession();
              this.$router.replace({ path: '/login' });
              return;
            }
            const msg = le?.response?.data?.message || 'Could not save pin location.';
            this.$toast?.error(`Profile saved, but pin location failed: ${msg}`);
            return;
          }
        }

        this.$toast?.success('Profile saved.');
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          clearPartnerSession();
          this.$router.replace({ path: '/login' });
          return;
        }
        const errs = e?.response?.data?.errors;
        const msg = e?.response?.data?.message || 'Could not save profile.';
        if (errs && typeof errs === 'object') {
          const firstKey = Object.keys(errs)[0];
          const first = Array.isArray(errs[firstKey]) ? errs[firstKey][0] : '';
          this.$toast?.error(first || msg);
        } else {
          this.$toast?.error(msg);
        }
      } finally {
        this.saving = false;
      }
    },
    validateSalonHours() {
      const days = this.form.hours || {};
      const anyOpen = Object.values(days).some((d) => d && d.open);
      if (!anyOpen) return false;
      return Object.values(days).every((d) => {
        if (!d || !d.open) return true;
        if (d.fullDay) return true;
        return Boolean(d.start && d.end);
      });
    },
    validateSalonExtras() {
      if (!String(this.form.referral || '').trim()) {
        this.$toast?.error('Please select how you heard about us.');
        return false;
      }
      if (!Array.isArray(this.form.serviceOffered) || !this.form.serviceOffered.length) {
        this.$toast?.error('Select at least one service you offer.');
        return false;
      }
      if (this.form.serviceOffered.includes('others') && !String(this.form.serviceOtherText || '').trim()) {
        this.$toast?.error('Please describe other services.');
        return false;
      }
      return true;
    },
    validateGroomerProfile() {
      const f = this.form;
      if (!f.primaryServiceType) {
        this.$toast?.error('Select your primary service type.');
        return false;
      }
      if (!f.petsGroomed) {
        this.$toast?.error('Select how many pets you have groomed.');
        return false;
      }
      if (!Array.isArray(f.petsServiced) || !f.petsServiced.length) {
        this.$toast?.error('Select at least one pet type you service.');
        return false;
      }
      if (!f.hasCertifications) {
        this.$toast?.error('Please answer the certification question.');
        return false;
      }
      if (f.hasCertifications === 'yes' && !String(f.certifications || '').trim()) {
        this.$toast?.error('Please enter your certification details.');
        return false;
      }
      if (!f.hasDriversLicense || !f.hasTools || !f.hasTaxId) {
        this.$toast?.error('Please complete driver’s license, tools, and tax ID questions.');
        return false;
      }
      if (!Array.isArray(f.preferredServiceType) || !f.preferredServiceType.length) {
        this.$toast?.error('Select at least one preferred service type.');
        return false;
      }
      if (!f.workCurrently || !f.hasReferences) {
        this.$toast?.error('Please answer work status and references.');
        return false;
      }
      if (!String(f.groomerLearnGrooming || '').trim()) {
        this.$toast?.error('Please describe where you learned grooming.');
        return false;
      }
      return true;
    },
  },
};
</script>

<style scoped>
.pp-sec-title {
  font-size: 15px;
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
}
.pp-sec-title:first-child {
  margin-top: 0;
}
.pp-label {
  font-size: 11px;
  display: block;
  margin-bottom: 4px;
  font-family: interregular, sans-serif;
  color: #333;
}
.pp-input {
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
}
.pp-input--readonly {
  background: #f5f6f8;
  color: #555;
}
.pp-select {
  appearance: auto;
}
.pp-textarea {
  resize: vertical;
  min-height: 80px;
}
.pp-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
}
.pp-check {
  font-size: 13px;
  margin: 0;
}
.pp-hours-row {
  display: grid;
  grid-template-columns: 140px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.pp-hours-row--off {
  opacity: 0.65;
}
.pp-hours-day {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 13px;
}
.pp-hours-times {
  display: flex;
  gap: 8px;
}
.pp-time {
  width: auto;
  max-width: 130px;
}
.pp-fullday {
  font-size: 12px;
  margin: 0;
  white-space: nowrap;
}
.pp-pill {
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 13px;
}
.pp-pill--on {
  border-color: #f8721f;
  background: rgba(248, 114, 31, 0.1);
  color: #c55a14;
}
.pp-save {
  border: none;
  border-radius: 10px;
  padding: 12px 28px;
  background: #f8721f;
  color: #fff;
  margin-top: 0.5rem;
}
.pp-save:disabled {
  opacity: 0.65;
}
.pp-secondary-btn {
  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 10px;
  padding: 10px 18px;
  background: #fff;
  color: #333;
  font-size: 11px;
}
.pp-secondary-btn:hover {
  border-color: #f8721f;
  color: #c55a14;
}
@media (max-width: 576px) {
  .pp-hours-row {
    grid-template-columns: 1fr;
  }
}
</style>
